import { useEffect, useState } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { AlertCircle, ArrowRight, BarChart3, Eye, EyeOff, Loader2, Send, Store, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { loadTenantBranding, logoUrl, useBranding } from "@/lib/branding";
import { setSlug } from "@/lib/tenant";

const FEATURES = [[Users, "Clienti e fedeltà"], [BarChart3, "Statistiche e report"], [Send, "Campagne multicanale"]];

const CompanySlot = ({ b }) => {
  const src = logoUrl(b, "company");
  if (!src && !b.company_name) return null;
  return (
    <div className="flex items-center gap-4" data-testid="tenant-company-logo-slot">
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Dati di</span>
      <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] py-2 pl-2 pr-4 backdrop-blur-md">
        {src ? <img src={src} alt="Logo azienda" data-testid="brand-logo-company" className="h-10 w-auto max-w-[140px] rounded-lg bg-white object-contain p-1.5" />
          : <span className="grid h-10 w-10 place-items-center rounded-lg bg-white/10"><Store className="h-5 w-5 text-slate-300" /></span>}
        {b.company_name && <span className="font-heading text-sm font-medium text-slate-100" data-testid="landing-company-name">{b.company_name}</span>}
      </div>
    </div>
  );
};

export const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</label>
    {children}
  </div>
);

export const inputCls = "h-12 w-full rounded-xl border border-slate-800 bg-slate-950/70 px-4 text-sm text-white placeholder:text-slate-600 outline-none transition-[border-color,box-shadow] focus:border-teal-500/80 focus:ring-2 focus:ring-teal-500/20";

export const AuthCard = ({ children, testId }) => (
  <div data-testid={testId} className="relative mx-auto w-full max-w-md animate-rise overflow-hidden rounded-3xl border border-slate-800/90 bg-slate-900/70 p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] backdrop-blur-2xl sm:p-10">
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
    {children}
  </div>
);

export const submitCls = "group flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 via-teal-600 to-emerald-500 font-semibold text-white shadow-lg shadow-teal-900/40 transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:opacity-70";

export const ForgotForm = ({ email: initial, onBack, tenant }) => {
  const [email, setEmail] = useState(initial);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { setMsg((await api.post("/auth/forgot-password", { email, tenant })).data.message); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <AuthCard testId="forgot-form">
      <form onSubmit={send}>
        <h2 className="font-heading text-2xl font-semibold text-white">Password dimenticata</h2>
        <p className="mt-1.5 text-sm text-slate-400">Ti inviamo via email un link per sceglierne una nuova, valido 1 ora.</p>
        <div className="mt-8 space-y-5">
          <Field id="forgot-email" label="Email"><input id="forgot-email" type="email" required data-testid="forgot-email-input" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          {msg && <div data-testid="forgot-success" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3.5 text-sm text-emerald-300">{msg}</div>}
          {error && <div data-testid="forgot-error" className="rounded-xl border border-red-500/30 bg-red-500/10 p-3.5 text-sm text-red-300">{error}</div>}
          <button type="submit" disabled={busy || !!msg} data-testid="forgot-submit-button" className={submitCls}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia link"}</button>
        </div>
        <button type="button" onClick={onBack} data-testid="forgot-back-link" className="mt-5 text-sm text-teal-400 hover:text-teal-300">← Torna all'accesso</button>
      </form>
    </AuthCard>
  );
};

export default function Login() {
  const { user, login } = useAuth();
  const b = useBranding();
  useEffect(() => { document.title = b.app_name ? `${b.app_name} · Accesso` : "Nouvelle CRM"; }, [b.app_name]);
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const { slug = "" } = useParams();
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setSlug(slug);
    loadTenantBranding(slug).then(() => setMissing(false)).catch(() => setMissing(true));
  }, [slug]);

  if (user) return <Navigate to={loc.state?.from || new URLSearchParams(loc.search).get("next") || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await login(email, password, slug); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#07090e] text-slate-200" data-testid="landing-page">
      <div className="pointer-events-none absolute inset-0">
        <div className="login-orb absolute -left-40 -top-40 h-[560px] w-[560px] rounded-full bg-blue-600/25 blur-[120px]" />
        <div className="login-orb absolute bottom-[-200px] left-[30%] h-[520px] w-[520px] rounded-full bg-emerald-500/20 blur-[130px]" style={{ animationDelay: "-6s" }} />
        <div className="absolute right-[-120px] top-1/4 h-[380px] w-[380px] rounded-full opacity-30 blur-[110px]" style={{ background: b.color }} />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "56px 56px", maskImage: "radial-gradient(ellipse at 30% 40%, #000 30%, transparent 75%)" }} />
      </div>

      <div className="relative mx-auto grid min-h-screen max-w-7xl gap-10 px-6 py-10 lg:grid-cols-12 lg:items-center lg:px-12">
        <section className="flex flex-col gap-10 lg:col-span-7 lg:pr-10">
          <div className="flex animate-rise justify-center lg:justify-start">
            <div className="relative inline-block">
              <div className="absolute -inset-10 rounded-full bg-[radial-gradient(closest-side,rgba(16,185,129,0.25),transparent_70%)] blur-2xl" />
              <img src="/nouvelle-crm-logo-dark.png" alt="Nouvelle CRM" data-testid="nouvelle-crm-logo" className="relative h-32 w-auto transition-transform duration-500 hover:scale-[1.03] sm:h-44 lg:h-56" />
            </div>
          </div>
          <div className="hidden max-w-xl animate-rise lg:block" style={{ animationDelay: "120ms" }}>
            <h1 className="font-heading text-4xl font-semibold leading-[1.1] tracking-tight text-white xl:text-5xl" data-testid="landing-title">{b.welcome_title}</h1>
            <p className="mt-5 text-base leading-relaxed text-slate-400" data-testid="landing-text">{b.welcome_text}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {FEATURES.map(([Icon, t]) => (
                <span key={t} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs text-slate-300"><Icon className="h-3.5 w-3.5 text-emerald-400" />{t}</span>
              ))}
            </div>
          </div>
          <div className="hidden animate-rise lg:block" style={{ animationDelay: "240ms" }}><CompanySlot b={b} /></div>
        </section>

        <section className="lg:col-span-5">
          {forgot ? <ForgotForm email={email} tenant={slug} onBack={() => setForgot(false)} /> : <form onSubmit={submit} data-testid="login-form" style={{ animationDelay: "180ms" }}
            className="relative mx-auto w-full max-w-md animate-rise overflow-hidden rounded-3xl border border-slate-800/90 bg-slate-900/70 p-8 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] backdrop-blur-2xl sm:p-10">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/60 to-transparent" />
            <h2 className="font-heading text-2xl font-semibold text-white" data-testid="landing-app-name">Accedi a {b.app_name}</h2>
            <p className="mt-1.5 text-sm text-slate-400">Inserisci le credenziali del tuo profilo.</p>
            {missing && <div data-testid="tenant-missing" className="mt-4 flex items-center gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3.5 text-sm text-amber-300"><AlertCircle className="h-4 w-4 shrink-0" />Circuito non trovato o disattivato: controlla il link di accesso.</div>}
            <div className="mt-8 space-y-5">
              <Field id="email" label="Email">
                <input id="email" type="email" required autoComplete="email" placeholder="nome@azienda.it" data-testid="login-email-input" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              <Field id="password" label="Password">
                <div className="relative">
                  <input id="password" type={show ? "text" : "password"} required autoComplete="current-password" data-testid="login-password-input" className={`${inputCls} pr-12`} value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" onClick={() => setShow(!show)} data-testid="toggle-password-visibility" aria-label={show ? "Nascondi password" : "Mostra password"}
                    className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-lg text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-200">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </Field>
              {error && <div data-testid="login-error" className="flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 p-3.5 text-sm text-red-300"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
              <button type="submit" disabled={busy} data-testid="login-submit-button"
                className="group flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 via-teal-600 to-emerald-500 font-semibold text-white shadow-lg shadow-teal-900/40 transition-[filter,transform] hover:brightness-110 active:scale-[0.99] disabled:opacity-70">
                {busy ? <><Loader2 className="h-4 w-4 animate-spin" />Accesso in corso...</> : <>Accedi<ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>}
              </button>
            </div>
            <button type="button" data-testid="forgot-password-link" onClick={() => setForgot(true)} className="mt-5 text-sm text-teal-400 transition-colors hover:text-teal-300">Password dimenticata?</button>
            {(b.company_name || b.logos?.company) && <div className="mt-8 border-t border-white/5 pt-6 lg:hidden"><CompanySlot b={b} /></div>}
          </form>}
          <p className="mt-6 text-center text-xs text-slate-600">Accesso riservato · i permessi dipendono dal tuo profilo</p>
        </section>
      </div>
    </div>
  );
}
